import { useState } from "react"
import { Chip } from "@/components/Molecules/Chip/Chip"
import { Icon } from "@/components/Atoms/Icon"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer"
import { Button } from "@/components/ui/button"

export interface LecturesFilterProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  categoryFilter: string
  onCategoryFilterChange: (value: string) => void
  categories: string[]
}

export const LecturesFilter: React.FC<LecturesFilterProps> = ({
  searchTerm,
  onSearchChange,
  categoryFilter,
  onCategoryFilterChange,
  categories,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [draftCategory, setDraftCategory] = useState("all")

  const hasActiveFilters = categoryFilter !== "all"

  const openDrawer = () => {
    setDraftCategory(categoryFilter)
    setDrawerOpen(true)
  }

  const applyFilters = () => {
    onCategoryFilterChange(draftCategory)
    setDrawerOpen(false)
  }

  const clearFilters = () => {
    onCategoryFilterChange("all")
    setDraftCategory("all")
    setDrawerOpen(false)
  }

  const activeChips: { key: string; label: string; onRemove: () => void }[] = []

  if (categoryFilter !== "all") {
    activeChips.push({
      key: "category",
      label: categoryFilter,
      onRemove: () => onCategoryFilterChange("all"),
    })
  }

  return (
    <div>
      {/* Mobile */}
      <div className="lg:hidden">
        <div className="flex items-center gap-3">
          <div className="relative flex flex-1 items-center gap-3 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Pesquisar palestras..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2.5 focus:outline-none"
            />
          </div>
          <button
            onClick={openDrawer}
            className="relative flex size-10 shrink-0 items-center justify-center rounded-lg border border-border bg-black/40"
          >
            <i
              className={`fi fi-ts-sliders-v flex items-center justify-center text-base ${hasActiveFilters ? "text-orange-500" : "text-white"}`}
            />
            {hasActiveFilters && (
              <span className="absolute -right-1 -top-1 size-3 rounded-full bg-orange-500" />
            )}
          </button>
        </div>
        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <div className="flex items-center gap-4">
          <div className="relative flex w-80 items-center gap-4 rounded-lg border border-border bg-black/40 px-4 text-white">
            <Icon size={14} name="search" />
            <input
              type="text"
              placeholder="Pesquisar palestras..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full bg-transparent py-2 focus:outline-none"
            />
          </div>

          <Select onValueChange={onCategoryFilterChange} value={categoryFilter}>
            <SelectTrigger
              className={`w-48 border-border bg-surface ${categoryFilter !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : ""}`}
            >
              <SelectValue placeholder="Todas as categorias" />
            </SelectTrigger>
            <SelectContent className="border-border bg-surface">
              <SelectItem value="all">Todas as categorias</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="whitespace-nowrap text-sm text-muted-foreground transition-colors hover:text-orange-500"
            >
              Limpar filtros
            </button>
          )}
        </div>

        {activeChips.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {activeChips.map((chip) => (
              <Chip key={chip.key} label={chip.label} onClose={chip.onRemove} />
            ))}
          </div>
        )}
      </div>

      {/* Mobile bottom sheet */}
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent className="border-white/10 bg-surface px-4 pb-2">
          <DrawerHeader>
            <DrawerTitle className="text-white">Filtros</DrawerTitle>
          </DrawerHeader>

          <div className="flex flex-col gap-4 px-4 pb-2">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-muted-foreground">Categoria</span>
              <Select onValueChange={setDraftCategory} value={draftCategory}>
                <SelectTrigger
                  className={`border-border bg-black/40 ${draftCategory !== "all" ? "border-orange-500 bg-orange-500/10 text-orange-500" : "text-white"}`}
                >
                  <SelectValue placeholder="Todas as categorias" />
                </SelectTrigger>
                <SelectContent className="border-border bg-surface">
                  <SelectItem value="all">Todas as categorias</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DrawerFooter>
            <Button
              onClick={applyFilters}
              className="w-full bg-orange-500 font-semibold text-white hover:bg-orange-600"
            >
              Aplicar filtro
            </Button>
            <Button
              onClick={clearFilters}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-white"
            >
              Limpar filtro
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  )
}
